import { useForm, usePage, router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableTableHead } from "@/components/ui/sortable-table-head"
import { FlashMessages } from "@/components/FlashMessages"
import { FormError } from "@/components/FormError"
import {
  Plus,
  Pencil,
  Trash2,
  Package,
} from "lucide-react"
import { useSortableData } from "@/hooks/useSortableData"
import { noAutofillProps } from "@/lib/form"
import type { AppPageProps } from "@/types"

interface Product {
  id: string
  name: string
  default_expiration_days: number | null
  created_at: string
}

interface ProductsPageProps extends AppPageProps {
  products: Product[]
}

type ProductSortKey = "name" | "created_at"

const productComparators: Record<ProductSortKey, (a: Product, b: Product) => number> = {
  name: (a, b) => a.name.localeCompare(b.name),
  created_at: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
}

export default function Products() {
  const { t } = useTranslation()
  const { auth, flash, products } = usePage<ProductsPageProps>().props
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)

  const {
    sortedData: sortedProducts,
    sortKey: productSortKey,
    sortDirection: productSortDirection,
    handleSort: handleProductSort,
  } = useSortableData(products, "name", productComparators)

  const createForm = useForm({
    name: "",
    default_expiration_days: "",
  })

  const editForm = useForm({
    name: "",
    default_expiration_days: "",
  })

  function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault()
    createForm.post("/products", {
      preserveState: true,
      onSuccess: () => {
        createForm.reset()
        setIsCreateDialogOpen(false)
      },
    })
  }

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editingProduct) return

    editForm.patch(`/products/${editingProduct.id}`, {
      preserveState: true,
      onSuccess: () => {
        setEditingProduct(null)
        editForm.reset()
      },
    })
  }

  function openEditDialog(product: Product) {
    setEditingProduct(product)
    editForm.setData({
      name: product.name,
      default_expiration_days: product.default_expiration_days?.toString() ?? "",
    })
  }

  function handleDeleteProduct(product: Product) {
    if (confirm(t("products.confirmDelete"))) {
      router.delete(`/products/${product.id}`)
    }
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("products.title")}</h2>
            <p className="text-muted-foreground">{t("products.subtitle")}</p>
          </div>

          {/* Create Dialog */}
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t("products.addButton")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleCreateSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("products.addButton")}</DialogTitle>
                  <DialogDescription>
                    {t("products.addDescription")}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="create-name">{t("common.name")}</Label>
                    <Input
                      id="create-name"
                      value={createForm.data.name}
                      onChange={(e) => createForm.setData("name", e.target.value)}
                      placeholder={t("products.namePlaceholder")}
                      required
                      {...noAutofillProps}
                    />
                    <FormError error={createForm.errors.name} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-default-expiration">{t("products.defaultExpirationDaysLabel")}</Label>
                    <Input
                      id="create-default-expiration"
                      type="number"
                      min="1"
                      value={createForm.data.default_expiration_days}
                      onChange={(e) => createForm.setData("default_expiration_days", e.target.value)}
                      placeholder={t("products.defaultExpirationDaysPlaceholder")}
                    />
                    <p className="text-sm text-muted-foreground">{t("products.defaultExpirationDaysHint")}</p>
                    <FormError error={createForm.errors.default_expiration_days} />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCreateDialogOpen(false)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={createForm.processing}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t("common.add")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Dialog */}
          <Dialog open={!!editingProduct} onOpenChange={(open) => !open && setEditingProduct(null)}>
            <DialogContent>
              <form onSubmit={handleEditSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("products.editProduct")}</DialogTitle>
                  <DialogDescription>{t("products.editDescription")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">{t("common.name")}</Label>
                    <Input
                      id="edit-name"
                      value={editForm.data.name}
                      onChange={(e) => editForm.setData("name", e.target.value)}
                      placeholder={t("products.namePlaceholder")}
                      required
                      {...noAutofillProps}
                    />
                    <FormError error={editForm.errors.name} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-default-expiration">{t("products.defaultExpirationDaysLabel")}</Label>
                    <Input
                      id="edit-default-expiration"
                      type="number"
                      min="1"
                      value={editForm.data.default_expiration_days}
                      onChange={(e) => editForm.setData("default_expiration_days", e.target.value)}
                      placeholder={t("products.defaultExpirationDaysPlaceholder")}
                    />
                    <p className="text-sm text-muted-foreground">{t("products.defaultExpirationDaysHint")}</p>
                    <FormError error={editForm.errors.default_expiration_days} />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingProduct(null)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={editForm.processing}>
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Products Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              {t("products.section")}
            </CardTitle>
            <CardDescription>{t("products.sectionDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {products.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {t("products.noProducts")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead
                      label={t("common.name")}
                      sortKey="name"
                      currentSortKey={productSortKey}
                      direction={productSortDirection}
                      onSort={handleProductSort}
                    />
                    <SortableTableHead
                      label={t("common.createdAt")}
                      sortKey="created_at"
                      currentSortKey={productSortKey}
                      direction={productSortDirection}
                      onSort={handleProductSort}
                    />
                    <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedProducts.map((product, index) => (
                    <TableRow key={product.id} className={index % 2 === 1 ? "bg-muted" : ""}>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell>{formatDate(product.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(product)}
                            title={t("products.editProduct")}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteProduct(product)}
                            className="text-destructive hover:text-destructive"
                            title={t("products.deleteProduct")}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
